import React, { useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Stack } from '@mui/material';
import TwSelect from '../../../../../components/TwSelects/TwSelects';
import useAuth from '../../../../../providers/Auth/Auth.provider';
import useNotifications from '../../../../../providers/Notifications/Notifications.provider';
import {
  DriverAckType,
  OwnerOperatorDriverAckType,
  TenantDriverSettingsFormValues
} from '../../../../../redux/api/tenantDriverSettings/tenantDriverSettings.model';
import {
  useGetTenantDriverSettingsQuery,
  useUpdateTenantDriverSettingsMutation
} from '../../../../../redux/api/tenantDriverSettings/tenantDriverSettingsApi';
import _ from 'lodash';

interface DriverAckSettingProps {
  isEnabled: boolean;
}

const DriverAckSetting: React.FC<DriverAckSettingProps> = ({ isEnabled }): JSX.Element => {
  const {
    data: { userData },
    getTenantSetting,
    updateTenantDriverSettings: patchTenantDriverSettings
  } = useAuth();
  const { addNotification } = useNotifications();

  // settings
  const ownerOperatorsEnabled = getTenantSetting('owner-operators');

  const [formValues, setFormValues] = useState<TenantDriverSettingsFormValues>({
    company_driver_ack_type: DriverAckType.ACK_DISABLED,
    owner_operator_driver_ack_type: OwnerOperatorDriverAckType.ACK_DISABLED
  });
  const [updated, setUpdated] = useState(false);

  // api
  const { data: tenantDriverSettings, isLoading } = useGetTenantDriverSettingsQuery();
  const [updateTenantDriverSettings, { isLoading: isUpdating }] =
    useUpdateTenantDriverSettingsMutation();

  useEffect(() => {
    if (isEnabled || isLoading) return;

    // if the setting is disabled, set the form values to the default values
    const newFormValues = {
      company_driver_ack_type: DriverAckType.ACK_DISABLED,
      owner_operator_driver_ack_type: OwnerOperatorDriverAckType.ACK_DISABLED
    };

    setFormValues(newFormValues);
    handleSubmit(newFormValues);
    return;
  }, [isEnabled, isLoading]);

  useEffect(() => {
    if (!isEnabled || updated || isLoading || !tenantDriverSettings) return;

    const companyAckDisabled =
      tenantDriverSettings?.data.company_driver_ack_type === DriverAckType.ACK_DISABLED;

    // if owner operator setting is disabled,
    // and company drivers setting has ACK_DISABLED value
    // let's update form values
    if (!ownerOperatorsEnabled && companyAckDisabled) {
      const newFormValues = {
        company_driver_ack_type: DriverAckType.CONFIRMATION,
        owner_operator_driver_ack_type: tenantDriverSettings.data.owner_operator_driver_ack_type
      };

      setFormValues(newFormValues);
      handleSubmit(newFormValues);
      return;
    }

    // if owner operator setting is disabled,
    // and company drivers setting does not have ACK_DISABLED value
    // let's keep the tenant driver settings
    if (!ownerOperatorsEnabled && !companyAckDisabled) {
      const newFormValues = {
        company_driver_ack_type: tenantDriverSettings.data.company_driver_ack_type,
        owner_operator_driver_ack_type: tenantDriverSettings.data.owner_operator_driver_ack_type
      };

      setFormValues(newFormValues);
      return;
    }

    const ownerOperatorAckDisabled =
      tenantDriverSettings?.data.owner_operator_driver_ack_type ===
      OwnerOperatorDriverAckType.ACK_DISABLED;

    // if owner operator setting is enabled,
    // and owner operator drivers and company drivers setting does not have ACK_DISABLED value
    // let's keep the tenant driver settings and show error
    if (ownerOperatorsEnabled && !ownerOperatorAckDisabled && !companyAckDisabled) {
      const newFormValues = {
        company_driver_ack_type: tenantDriverSettings.data.company_driver_ack_type,
        owner_operator_driver_ack_type: tenantDriverSettings.data.owner_operator_driver_ack_type
      };

      setFormValues(newFormValues);
      return;
    }

    // if owner operator setting is enabled,
    // and owner operator drivers setting has ACK_DISABLED value
    // and company drivers setting does not have ACK_DISABLED value
    // let's keep tenant driver settings
    if (ownerOperatorsEnabled && ownerOperatorAckDisabled && !companyAckDisabled) {
      const newFormValues = {
        company_driver_ack_type: tenantDriverSettings.data.company_driver_ack_type,
        owner_operator_driver_ack_type: tenantDriverSettings.data.owner_operator_driver_ack_type
      };

      setFormValues(newFormValues);
      return;
    }

    // if owner operator setting is enabled,
    // and owner operator drivers setting does not have ACK_DISABLED value
    // and company drivers setting does not have ACK_DISABLED value
    // let's update form values
    if (ownerOperatorsEnabled && ownerOperatorAckDisabled && companyAckDisabled) {
      handleSubmit({
        company_driver_ack_type: DriverAckType.ACK_DISABLED,
        owner_operator_driver_ack_type: OwnerOperatorDriverAckType.ACK_DISABLED
      });
      return;
    }

    // if owner operator setting is enabled
    if (ownerOperatorsEnabled) {
      const newFormValues = {
        company_driver_ack_type: tenantDriverSettings.data.company_driver_ack_type,
        owner_operator_driver_ack_type: tenantDriverSettings.data.owner_operator_driver_ack_type
      };

      setFormValues(newFormValues);
      return;
    }
  }, [isEnabled, ownerOperatorsEnabled, tenantDriverSettings]);

  const getOptions = useMemo(() => {
    const options = [
      { value: 'CONFIRMATION', label: 'Can confirm receipt' },
      { value: 'ACCEPT_REJECT', label: 'Can accept or reject' }
    ];

    if (ownerOperatorsEnabled) {
      options.push({ value: 'ACK_DISABLED', label: 'Disable acknowledgement' });
    }

    return options;
  }, [ownerOperatorsEnabled]);

  const showError = (): void => {
    addNotification({
      message: 'Acknowledgement should be enabled for at least one driver type',
      variant: 'error'
    });
  };

  const handleSubmit = (newFormValues: TenantDriverSettingsFormValues): void => {
    if (!userData || !userData.tenant?.id || isUpdating || !tenantDriverSettings) return;

    const companyAckDisabled = newFormValues.company_driver_ack_type === DriverAckType.ACK_DISABLED;
    const ownerOperatorAckDisabled =
      newFormValues.owner_operator_driver_ack_type === OwnerOperatorDriverAckType.ACK_DISABLED;

    if (isEnabled && ownerOperatorsEnabled && ownerOperatorAckDisabled && companyAckDisabled) {
      showError();

      setFormValues({
        company_driver_ack_type: tenantDriverSettings?.data.company_driver_ack_type,
        owner_operator_driver_ack_type: tenantDriverSettings?.data.owner_operator_driver_ack_type
      });

      return;
    }

    const currentFormValues = {
      company_driver_ack_type: tenantDriverSettings?.data.company_driver_ack_type,
      owner_operator_driver_ack_type: tenantDriverSettings?.data.owner_operator_driver_ack_type
    };

    const update = !_.isEqual(currentFormValues, newFormValues);

    if (!update) return;

    updateTenantDriverSettings({
      ...newFormValues,
      tenant_id: userData.tenant.id
    })
      .then((response) => {
        addNotification({
          message: 'Driver acknowledgement settings updated successfully',
          variant: 'success'
        });

        if ('data' in response && response.data) {
          patchTenantDriverSettings({
            company_driver_ack_type: response.data.content.company_driver_ack_type,
            owner_operator_driver_ack_type: response.data.content.owner_operator_driver_ack_type
          });
        }

        setUpdated(true);
      })
      .catch(() => {
        addNotification({
          message: 'Something went wrong while updating driver acknowledgement settings',
          variant: 'error'
        });
      });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '40px' }}>
      {isLoading ? (
        <Stack
          flexDirection={'row'}
          justifyContent={'center'}
          sx={{ width: '50px', padding: '10px' }}
          className="tid-hos-tooltip-loading"
        >
          <CircularProgress size={20} />
        </Stack>
      ) : (
        <>
          <TwSelect
            id="companyDriverAckType"
            name="companyDriverAckType"
            label="Company drivers"
            value={formValues.company_driver_ack_type}
            optionList={getOptions}
            onInputChange={(e): void => {
              const newFormValues = {
                ...formValues,
                company_driver_ack_type: e.target.value as DriverAckType
              };

              setFormValues(newFormValues);
              handleSubmit(newFormValues);
            }}
            disabled={!isEnabled || isUpdating}
          />
          {ownerOperatorsEnabled && (
            <TwSelect
              id="ownerOperatorDriverAckType"
              name="ownerOperatorDriverAckType"
              label="Owner operator drivers"
              value={formValues.owner_operator_driver_ack_type}
              optionList={getOptions}
              onInputChange={(e): void => {
                const newFormValues = {
                  ...formValues,
                  owner_operator_driver_ack_type: e.target.value as OwnerOperatorDriverAckType
                };

                setFormValues(newFormValues);
                handleSubmit(newFormValues);
              }}
              disabled={!isEnabled || isUpdating}
            />
          )}
        </>
      )}
    </Box>
  );
};

export default DriverAckSetting;
